import { getUserInspirationBoards } from "@/services/discovery";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

// 全局缓存对象
const boardsCache = {
  data: null, // 缓存的画板数据
  lastPathname: null, // 上次请求时的路由路径
  loading: false, // 是否正在请求中
  promise: null, // 正在进行的请求 Promise
  error: null, // 错误信息
};

/**
 * 判断路由是否在允许缓存的路径下
 * 包括：/discovery、/follow、/discovery/boards 等
 */
function isCacheableRoute(pathname) {
  if (!pathname) return false;
  return (
    pathname.startsWith("/discovery") ||
    pathname.startsWith("/follow") ||
    pathname === "/guide"
  );
}

/**
 * 获取灵感画板列表的 Hook
 *
 * 特性：
 * 1. 路由缓存：在 /discovery 路径下切换时使用缓存，从其他页面进入时重新请求
 * 2. 请求去重：多个组件同时调用时只发起一次请求
 * 3. 自动过滤私有画板
 * 4. 支持手动刷新缓存
 *
 * @param {Object} user - 用户对象
 * @returns {Object} { boards: 画板列表, loading: 加载状态, error: 错误信息, refresh: 刷新函数 }
 */
export function useInspirationBoards(user) {
  const router = useRouter();
  const [boards, setBoards] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const userIdRef = useRef(user?.user_id);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // 如果用户未登录，直接返回空数组
    if (!user || !user.user_id) {
      setBoards([]);
      setLoading(false);
      setError(null);
      return;
    }

    // 如果用户 ID 发生变化，清空缓存
    if (userIdRef.current !== user.user_id) {
      userIdRef.current = user.user_id;
      invalidateInspirationBoardsCache();
    }

    const fetchBoards = async () => {
      const currentPathname = router.pathname;

      // 检查缓存是否有效
      // 缓存有效的条件：
      // 1. 有缓存数据
      // 2. 上次请求的路径在允许缓存的路径下（/discovery、/follow 等）
      // 3. 当前路径也在允许缓存的路径下
      const isCacheValid =
        boardsCache.data !== null &&
        isCacheableRoute(boardsCache.lastPathname) &&
        isCacheableRoute(currentPathname);

      if (isCacheValid) {
        // 使用缓存数据
        if (mountedRef.current) {
          setBoards(boardsCache.data);
          setLoading(false);
          setError(boardsCache.error);
        }
        return;
      }

      // 如果正在请求中，等待现有请求完成
      if (boardsCache.loading && boardsCache.promise) {
        setLoading(true);
        try {
          const result = await boardsCache.promise;
          if (mountedRef.current) {
            setBoards(result);
            setLoading(false);
            setError(null);
          }
        } catch (err) {
          if (mountedRef.current) {
            setBoards([]);
            setLoading(false);
            setError(err);
          }
        }
        return;
      }

      // 发起新请求
      setLoading(true);
      boardsCache.loading = true;

      const requestPromise = (async () => {
        try {
          const result = await getUserInspirationBoards();
          const fetchedBoards = result.boards || [];

          // 过滤掉私有画板
          const publicBoards = fetchedBoards.filter(
            (board) => board.is_private <= 0
          );

          // 更新缓存
          boardsCache.data = publicBoards;
          boardsCache.lastPathname = router.pathname; // 记录请求时的路由
          boardsCache.error = null;
          boardsCache.loading = false;
          boardsCache.promise = null;

          return publicBoards;
        } catch (err) {
          console.error("Failed to fetch inspiration boards:", err);

          // 缓存错误状态
          boardsCache.data = [];
          boardsCache.lastPathname = router.pathname; // 记录请求时的路由
          boardsCache.error = err;
          boardsCache.loading = false;
          boardsCache.promise = null;

          throw err;
        }
      })();

      boardsCache.promise = requestPromise;

      try {
        const result = await requestPromise;
        if (mountedRef.current) {
          setBoards(result);
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        if (mountedRef.current) {
          setBoards([]);
          setLoading(false);
          setError(err);
        }
      }
    };

    fetchBoards();
  }, [user?.user_id, router.pathname]); // 依赖 user_id 和路由，路由变化时重新检查缓存

  // 手动刷新函数
  const refresh = () => {
    invalidateInspirationBoardsCache();
    // 触发重新请求（通过修改一个状态来触发 useEffect）
    setBoards(null);
    setLoading(true);
  };

  return {
    boards: boards || [],
    loading,
    error,
    refresh,
  };
}

/**
 * 手动清空缓存
 * 使用场景：
 * 1. 用户创建/删除画板后
 * 2. 用户修改画板隐私设置后
 * 3. 用户登出时
 */
export function invalidateInspirationBoardsCache() {
  boardsCache.data = null;
  boardsCache.lastPathname = null;
  boardsCache.loading = false;
  boardsCache.promise = null;
  boardsCache.error = null;
}

/**
 * 预加载画板数据
 * 使用场景：用户登录成功后立即预加载
 *
 * @param {string} pathname - 当前路由路径（可选）
 */
export function prefetchInspirationBoards(pathname = "/discovery") {
  // 如果缓存有效或正在请求中，不重复请求
  if (boardsCache.data !== null || boardsCache.loading) {
    return Promise.resolve(boardsCache.data);
  }

  // 发起预加载请求
  boardsCache.loading = true;

  const requestPromise = getUserInspirationBoards()
    .then((result) => {
      const fetchedBoards = result.boards || [];
      const publicBoards = fetchedBoards.filter(
        (board) => board.is_private <= 0
      );

      boardsCache.data = publicBoards;
      boardsCache.lastPathname = pathname; // 记录预加载时的路由
      boardsCache.error = null;
      boardsCache.loading = false;
      boardsCache.promise = null;

      return publicBoards;
    })
    .catch((err) => {
      console.error("Failed to prefetch inspiration boards:", err);
      boardsCache.data = [];
      boardsCache.lastPathname = pathname;
      boardsCache.error = err;
      boardsCache.loading = false;
      boardsCache.promise = null;
      return [];
    });

  boardsCache.promise = requestPromise;
  return requestPromise;
}
